// Welle 4 — Admin V2: modular platform dashboard.
// Two modes: Betreiber (operator, visual health) / Entwickler (developer,
// technical depth). Every number is computed LIVE by the backend — the
// page renders whatever modules `/admin/dashboard/overview` returns, so
// new registries/queues appear without touching this file's data flow.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Car, Image as ImageIcon, Sparkles, RefreshCw, Map as MapIcon,
} from "lucide-react";
import { fetchDashboardOverview } from "../api/adminDashboardApi";
import {
  StatCard, SectionCard, Pill, EmptyHint, coverageStatus, formatDateTime,
} from "../components/admin/v2/AdminV2Kit";
import CanonicalCoverageCard from "../components/admin/v2/CanonicalCoverageCard";
import PendingQueueCard from "../components/admin/v2/PendingQueueCard";
import GeoQueueCard from "../components/admin/v2/GeoQueueCard";
import VisionQueueCard from "../components/admin/v2/VisionQueueCard";
import WorkersCard from "../components/admin/v2/WorkersCard";
import ImportsCard from "../components/admin/v2/ImportsCard";
import PlatformHealthCard from "../components/admin/v2/PlatformHealthCard";

const MODE_KEY = "dealalert.admin.mode";

// Welle 4.1 — operator health rows: business language only, traffic
// lights, short explanations. All technical detail lives in dev mode.
function buildHealthRows(m, t) {
  const jobs = m.workers?.jobs || [];
  const failed = jobs.filter(
    (j) => j.last_status === "failed" || j.last_status === "error",
  ).length;
  const warn = jobs.filter((j) => !j.last_status).length;
  const active = jobs.length - failed - warn;
  const automationPct = jobs.length
    ? Math.round(((active + warn * 0.5) / jobs.length) * 100)
    : null;
  const openTasks =
    (m.pending?.total ?? 0)
    + (m.geo_queue?.total ?? 0)
    + (m.damage_queue?.suspect_total ?? 0);
  const openTasksPct = openTasks === 0 ? 100 : openTasks <= 10 ? 90 : openTasks <= 50 ? 75 : 50;
  const importsFailed = m.imports?.failed_24h ?? 0;
  return [
    {
      id: "data-quality",
      pct: m.canonical?.avg_coverage_pct,
      label: t("health.dataQuality"),
      value: m.canonical?.avg_coverage_pct != null ? `${m.canonical.avg_coverage_pct} %` : "—",
      desc: t("health.dataQualityDesc"),
    },
    {
      id: "images",
      pct: m.images?.alive_pct,
      label: t("health.images"),
      value: m.images?.alive_pct != null ? `${m.images.alive_pct} %` : "—",
      desc: t("health.imagesDesc"),
    },
    {
      id: "automation",
      pct: automationPct,
      label: t("health.automation"),
      value: t("health.automationValue", { active, total: jobs.length }),
      desc: t("health.automationDesc", { warn, failed }),
    },
    {
      id: "imports",
      pct: importsFailed > 0 ? 60 : 100,
      label: t("health.imports"),
      value: t("health.importsValue", {
        runs: m.imports?.runs_24h ?? 0,
        failed: importsFailed,
      }),
      desc: t("health.importsDesc"),
    },
    {
      id: "open-tasks",
      pct: openTasksPct,
      label: t("health.openTasks"),
      value: t("health.openTasksValue", { count: openTasks }),
      desc: t("health.openTasksDesc"),
    },
  ];
}

export default function AdminPlatformDashboardPage() {
  const { t } = useTranslation("admin");
  const [mode, setMode] = useState(
    () => localStorage.getItem(MODE_KEY) || "operator",
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setData(await fetchDashboardOverview());
      setError(false);
    } catch {
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const switchMode = (m) => {
    setMode(m);
    localStorage.setItem(MODE_KEY, m);
  };

  const developer = mode === "developer";
  const m = data?.modules || {};
  // Welle 4.3 P0-Hotfix — a failed/timed-out listings module must show
  // "Daten konnten nicht geladen werden", NEVER "0 Inserate".
  const listingsFailed =
    !!data
    && (!m.listings?.ok
      || m.listings?.counts_ok === false
      || m.listings?.active_total == null);
  const fmtCount = (v) => (v == null ? "—" : v.toLocaleString("de-AT"));

  return (
    <div className="space-y-6" data-testid="adminv2-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold text-zinc-100">
            <LayoutDashboard size={20} className="text-amber-400" />
            {t("title")}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">{t(`modeHint.${mode}`)}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <div className="flex rounded-full border border-zinc-800 bg-zinc-900/60 p-0.5">
            {["operator", "developer"].map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`adminv2-mode-${k}`}
                onClick={() => switchMode(k)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  mode === k
                    ? "bg-amber-400 text-zinc-950"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {t(`modes.${k}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="adminv2-refresh"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50 transition-colors"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            {t("refresh")}
          </button>
        </div>
      </div>

      {error && (
        <EmptyHint testid="adminv2-error">{t("loadError")}</EmptyHint>
      )}
      {!data && !error && (
        <div className="py-16 text-center text-sm text-zinc-500" data-testid="adminv2-loading">
          {t("loading")}
        </div>
      )}

      {data && (
        <>
          {/* Row 1 — System Health stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              testid="adminv2-stat-listings"
              title={t("modules.listings.title")}
              icon={Car}
              value={listingsFailed ? "—" : fmtCount(m.listings?.active_total)}
              sub={
                listingsFailed ? (
                  <span
                    className="block text-rose-300"
                    data-testid="adminv2-listings-error"
                  >
                    {t("modules.listings.loadFailed")}
                  </span>
                ) : (
                  <>
                    <span className="block" data-testid="adminv2-listings-split">
                      {t("modules.listings.auto")}{" "}
                      {fmtCount(m.listings?.active_auto)}
                      {" · "}
                      {t("modules.listings.immo")}{" "}
                      {fmtCount(m.listings?.active_immo)}
                      {" ("}
                      {t("modules.listings.buy")}{" "}
                      {fmtCount(m.listings?.active_immo_buy)}
                      {" · "}
                      {t("modules.listings.rent")}{" "}
                      {fmtCount(m.listings?.active_immo_rent)}
                      {")"}
                    </span>
                    <span className="block">
                      {Object.entries(m.listings?.active_by_country || {})
                        .map(([c, n]) => `${c} ${fmtCount(n)}`)
                        .join(" · ") || "—"}
                    </span>
                  </>
                )
              }
              status={listingsFailed ? "degraded" : "healthy"}
            />
            <StatCard
              testid="adminv2-stat-images"
              title={t("modules.images.title")}
              icon={ImageIcon}
              value={m.images?.alive_pct != null ? `${m.images.alive_pct} %` : "—"}
              sub={`${t("modules.images.alivePct")} · ${(m.images?.breakdown?.dead_on_check ?? 0)} ${t("modules.images.dead_on_check")}`}
              status={
                m.images?.alive_pct == null
                  ? "degraded"
                  : m.images.alive_pct >= 80
                    ? "healthy"
                    : "attention"
              }
            />
            <StatCard
              testid="adminv2-stat-ai"
              title={t("modules.ai.title")}
              icon={Sparkles}
              value={
                m.ai?.insight_coverage_pct != null
                  ? `${m.ai.insight_coverage_pct} %`
                  : "—"
              }
              sub={`${t("modules.ai.coverage")} · ${m.ai?.conversations_30d ?? 0} ${t("modules.ai.conversations")}`}
              status={coverageStatus(m.ai?.insight_coverage_pct)}
            />
            <StatCard
              testid="adminv2-stat-heatmap"
              title={t("modules.heatmap.title")}
              icon={MapIcon}
              value={(m.heatmap?.buckets_total ?? 0).toLocaleString("de-AT")}
              sub={
                <>
                  {t("modules.heatmap.buckets")} ·{" "}
                  {Object.entries(m.heatmap?.by_country || {})
                    .map(([c, n]) => `${c} ${n}`)
                    .join(" · ") || "—"}
                </>
              }
              status={m.heatmap?.ok ? "healthy" : "degraded"}
            />
          </div>

          {/* Welle 4.1 — strict mode separation:
              Betreiber → traffic lights, KPIs, platform health only.
              Entwickler → registries, queues, workers, full detail. */}
          {!developer && <PlatformHealthCard rows={buildHealthRows(m, t)} />}

          {developer && (
            <>
              {/* Row 2 — Canonical Engine (full width) */}
              <CanonicalCoverageCard data={m.canonical} developer />

              {/* Row 3 — Queues */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <PendingQueueCard summary={m.pending} developer />
                <GeoQueueCard summary={m.geo_queue} developer />
                <VisionQueueCard summary={m.vision_queue} developer />
                <SectionCard
                  title={t("modules.damage_queue.title")}
                  status={(m.damage_queue?.suspect_total ?? 0) > 0 ? "attention" : "healthy"}
                  testid="adminv2-damage-card"
                  actions={
                    <div className="text-2xl font-semibold text-zinc-100 tabular-nums">
                      {m.damage_queue?.suspect_total ?? 0}
                    </div>
                  }
                >
                  {(m.damage_queue?.suspect_total ?? 0) === 0 ? (
                    <EmptyHint testid="adminv2-damage-empty">
                      {t("modules.damage_queue.empty")}
                    </EmptyHint>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(m.damage_queue?.by_reason || {}).map(([r, n]) => (
                        <Pill key={r} tone="rose">
                          {t(`modules.damage_queue.reasons.${r}`, { defaultValue: r })} · {n}
                        </Pill>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-zinc-500">
                    {m.damage_queue?.damaged_active ?? 0}{" "}
                    {t("modules.damage_queue.damagedActive")}
                  </div>
                </SectionCard>
              </div>

              {/* Row 4 — Pipelines */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ImportsCard data={m.imports} developer />
                <WorkersCard data={m.workers} developer />
              </div>
            </>
          )}

          <div className="text-right text-[11px] text-zinc-600">
            {t("generatedAt")}: {formatDateTime(data.generated_at)}
          </div>
        </>
      )}
    </div>
  );
}
